import { ExternalLink, MapPin } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { Lead } from '../types'
import { KANBAN_COLUMNS } from '../types'

function getInitials(lead: Lead): string {
  const first = lead.first_name?.[0] ?? ''
  const last = lead.last_name?.[0] ?? ''
  return (first + last).toUpperCase() || '?'
}

function statusLabel(status: Lead['status']): string {
  const col = KANBAN_COLUMNS.find((c) => c.id === status)
  return col?.label ?? status.replace(/_/g, ' ')
}

function statusBadgeClass(status: Lead['status']): string {
  switch (status) {
    case 'won':
      return 'bg-emerald-500/12 text-emerald-300 border-emerald-500/25'
    case 'lost':
      return 'bg-red-500/12 text-red-300 border-red-500/25'
    case 'meeting_booked':
      return 'bg-orange-100 text-orange-700 border-orange-200'
    case 'qualified':
      return 'bg-[rgba(28,63,255,0.12)] text-[var(--color-cobalt-bright)] border-[rgba(28,63,255,0.25)]'
    case 'replied':
      return 'bg-indigo-100 text-indigo-700 border-indigo-200'
    case 'connection_sent':
    case 'connected':
    case 'dm_sent':
      return 'bg-[rgba(28,63,255,0.12)] text-[var(--color-cobalt-bright)] border-[rgba(28,63,255,0.25)]'
    default:
      return 'bg-[rgba(255,255,255,0.05)] text-[var(--color-silver-bright)] border-border'
  }
}

type LeadProfileProps = {
  readonly lead: Lead
}

export function LeadProfile({ lead }: LeadProfileProps) {
  const displayName =
    lead.full_name ??
    ([lead.first_name, lead.last_name].filter(Boolean).join(' ') || (lead.headline?.split(' at ')[0] ?? 'Unnamed Lead'))

  return (
    <div className="space-y-4">
      {/* Avatar + name block */}
      <div className="flex items-start gap-4">
        <Avatar className="h-16 w-16 shrink-0 ring-2 ring-muted">
          {lead.photo_url && <AvatarImage src={lead.photo_url} alt={displayName} />}
          <AvatarFallback className="text-xl font-semibold">{getInitials(lead)}</AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1 space-y-1">
          <h2 className="text-base font-semibold leading-tight">{displayName}</h2>
          {(lead.headline ?? lead.job_title) && (
            <p className="text-sm text-muted-foreground leading-snug">
              {lead.headline ?? lead.job_title}
            </p>
          )}
          {lead.company && (
            <p className="text-xs text-muted-foreground">{lead.company}</p>
          )}
        </div>
      </div>

      {/* Badges row. No connection-degree badge: leads carry no degree data, so it would always guess. */}
      <div className="flex flex-wrap gap-2 items-center">
        <Badge className={cn('text-xs', statusBadgeClass(lead.status))}>
          {statusLabel(lead.status)}
        </Badge>
        {lead.location && (
          <Badge variant="outline" className="text-xs font-normal">
            <MapPin className="mr-1 h-3 w-3 shrink-0" />
            {lead.location}
          </Badge>
        )}
        {lead.campaign && (
          <Badge variant="secondary" className="text-xs font-normal">
            {lead.campaign}
          </Badge>
        )}
      </div>

      {/* LinkedIn button */}
      <Button variant="outline" size="sm" className="w-full text-xs" asChild>
        <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
          View LinkedIn Profile
        </a>
      </Button>
    </div>
  )
}
